import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { API_BASE_URL, API_ENDPOINTS } from '../config/apiConfig';

interface TrackerStats {
  tracker: string;
  totalActivities: number;
  completedActivities: number;
  pendingActivities: number;
  tokensGenerated: number;
  activeMemberCount: number;
  averageTokensPerActivity: number;
}

interface Activity {
  id: number;
  activityName: string;
  memberName: string;
  category: string;
  tokens: number;
  date: string;
  status: 'pending' | 'approved' | 'rejected';
  tracker: string;
  description: string;
}

interface TrackerPerformance {
  tracker: string;
  monthlyData: Array<{
    month: string;
    activities: number;
    tokens: number;
  }>;
  categoryBreakdown: Array<{
    category: string;
    count: number;
    tokens: number;
  }>;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];
const TRACKERS = ['Ana G.', 'Jasson M.', 'Alex O.', 'Eliane A.', 'Catalina M.', 'IT'];

export const TrackerDashboard: React.FC = () => {
  const [selectedTracker, setSelectedTracker] = useState('Ana');
  const [timeRange, setTimeRange] = useState('3');

  const { data: trackerStats, isLoading: statsLoading } = useQuery<TrackerStats[]>({
    queryKey: ['tracker-stats', timeRange],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.trackerStats}?period=${timeRange}`);
      if (!response.ok) throw new Error('Failed to fetch tracker stats');
      return response.json();
    }
  });

  const { data: recentActivities } = useQuery<Activity[]>({
    queryKey: ['tracker-activities', selectedTracker],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.trackerActivitiesAlt}?tracker=${encodeURIComponent(selectedTracker)}&limit=10`);
      if (!response.ok) throw new Error('Failed to fetch activities');
      return response.json();
    }
  });

  const { data: performance } = useQuery<TrackerPerformance>({
    queryKey: ['tracker-performance', selectedTracker, timeRange],
    queryFn: async () => {
      const response = await fetch(`${API_BASE_URL}/api${API_ENDPOINTS.trackerPerformance}?tracker=${encodeURIComponent(selectedTracker)}&period=${timeRange}`);
      if (!response.ok) throw new Error('Failed to fetch performance data');
      return response.json();
    }
  });

  if (statsLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
    </div>
  );

  const currentTrackerStats = trackerStats?.find(t => t.tracker === selectedTracker);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900">Tracker Dashboard</h1>
        <div className="flex space-x-4">
          <select
            value={selectedTracker}
            onChange={(e) => setSelectedTracker(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            {TRACKERS.map(tracker => (
              <option key={tracker} value={tracker}>{tracker}</option>
            ))}
          </select>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
          >
            <option value="1">Last month</option>
            <option value="3">Last 3 months</option>
            <option value="6">Last 6 months</option>
            <option value="12">Last 12 months</option>
          </select>
        </div>
      </div>

      {/* Tracker Overview Cards */}
      {currentTrackerStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Activities</p>
                <p className="text-2xl font-semibold text-gray-900">{currentTrackerStats.totalActivities}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Completed</p>
                <p className="text-2xl font-semibold text-gray-900">{currentTrackerStats.completedActivities}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Tokens Generated</p>
                <p className="text-2xl font-semibold text-gray-900">{currentTrackerStats.tokensGenerated}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Members</p>
                <p className="text-2xl font-semibold text-gray-900">{currentTrackerStats.activeMemberCount}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All Trackers Comparison */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Tracker Performance Comparison</h3>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={trackerStats}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="tracker" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="completedActivities" fill="#8884d8" name="Completed Activities" />
            <Bar dataKey="tokensGenerated" fill="#82ca9d" name="Tokens Generated" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Performance Charts for Selected Tracker */}
      {performance && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monthly Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Activity Trend - {selectedTracker}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={performance.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="activities" fill="#8884d8" name="Activities" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Category Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Category Distribution - {selectedTracker}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={performance.categoryBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ category, count }) => `${category}: ${count}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {performance.categoryBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Recent Activities Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Recent Activities - {selectedTracker}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Activity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Member</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tokens</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentActivities?.map((activity) => (
                <tr key={activity.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{activity.activityName}</div>
                    <div className="text-sm text-gray-500 truncate max-w-xs">{activity.description}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {activity.memberName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {activity.category}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {activity.tokens}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(activity.date).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      activity.status === 'approved' 
                        ? 'bg-green-100 text-green-800'
                        : activity.status === 'pending'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {activity.status === 'pending' && (
                      <div className="flex space-x-2">
                        <button className="text-green-600 hover:text-green-900">Approve</button>
                        <button className="text-red-600 hover:text-red-900">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tracker Performance Summary */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All Trackers Summary</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-6">
          {trackerStats?.map((tracker) => (
            <div 
              key={tracker.tracker} 
              className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                selectedTracker === tracker.tracker 
                  ? 'border-blue-500 bg-blue-50' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              onClick={() => setSelectedTracker(tracker.tracker)}
            >
              <h4 className="font-semibold text-gray-900 mb-3">{tracker.tracker}</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Total Activities:</span>
                  <span className="font-medium">{tracker.totalActivities}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Completed:</span>
                  <span className="font-medium">{tracker.completedActivities}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Tokens Generated:</span>
                  <span className="font-medium">{tracker.tokensGenerated}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Active Members:</span>
                  <span className="font-medium">{tracker.activeMemberCount}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ 
                      width: `${tracker.totalActivities > 0 ? (tracker.completedActivities / tracker.totalActivities) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <div className="text-xs text-gray-500 text-center">
                  {tracker.totalActivities > 0 
                    ? `${((tracker.completedActivities / tracker.totalActivities) * 100).toFixed(1)}% completion rate`
                    : 'No activities'
                  }
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};